// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — what a stream may be used for, as reviewed for the media plans.
 *
 * Each release's `.dsa` carries a `rights` block (source of truth) and the
 * stream index copies it onto the row, so the app can answer without a
 * second fetch. The shape and the decisions behind it are in
 * docs/AYNI_XR_MEDIA_RIGHTS_AUDIT.md §7 and §13: one verdict per use (web
 * publication by an outlet, broadcast, paid renders we sell), the exact
 * credit line a render must carry, and who reviewed it and when.
 *
 * Verdicts are informational until the first Media sale (§13 Q15). Once
 * {@link RIGHTS_GATING} is on, a paid render refuses any layer whose
 * commercial verdict is not cleared, and a stream without a review (no
 * block, a malformed one, or a passed recheck date) counts as needs check.
 * Web and broadcast verdicts are shown, never enforced.
 */

export type RightsVerdict = 'cleared' | 'cleared_with_credit' | 'needs_check' | 'not_cleared'
export type RightsUse = 'web' | 'broadcast' | 'commercial'
export type CreditPlacement = 'on-image' | 'caption' | 'end-card'
export type CreditRequirement = 'required' | 'requested' | 'none'

export const RIGHTS_CONDITIONS = [
  'indicate-modification', 'no-endorsement', 'not-official-warning', 'show-update-date',
  'notify-pi', 'cite-doi', 'link-required', 'non-commercial',
] as const
export type RightsCondition = (typeof RIGHTS_CONDITIONS)[number]

export interface RightsLicence {
  readonly name: string
  readonly url: string | null
  readonly holder: string | null
}

export interface StreamRights {
  readonly web: RightsVerdict
  readonly broadcast: RightsVerdict
  readonly commercial: RightsVerdict
  readonly credit: {
    readonly required: CreditRequirement
    /** The exact line a render must carry (at most 400 characters, as attribution.text). */
    readonly line: string
    /** The renderer always burns the credit in (§13 Q10); other placements are extra. */
    readonly placement: CreditPlacement
  }
  readonly licences: readonly RightsLicence[]
  readonly conditions: readonly RightsCondition[]
  readonly reviewedBy: string | null
  /** YYYY-MM-DD. */
  readonly reviewedAt: string | null
  /** YYYY-MM-DD; once passed, the stream counts as needs check again. */
  readonly recheckBy: string | null
  readonly notes: string | null
}

/**
 * Off until the first Media sale (owner, 8 October 2026). Flip it in the
 * same commit that announces gating in the master record and the manual.
 */
export const RIGHTS_GATING = false

/** The cap DSA 1.7 puts on `attribution.text`, shared by every app. */
export const CREDIT_LINE_MAX = 400

const VERDICTS: readonly RightsVerdict[] = ['cleared', 'cleared_with_credit', 'needs_check', 'not_cleared']
const PLACEMENTS: readonly CreditPlacement[] = ['on-image', 'caption', 'end-card']
const REQUIREMENTS: readonly CreditRequirement[] = ['required', 'requested', 'none']
const DAY = /^\d{4}-\d{2}-\d{2}$/

const record = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null
const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null
const day = (value: unknown): string | null => {
  const s = text(value)
  return s && DAY.test(s) ? s : null
}
const verdict = (value: unknown): RightsVerdict | null =>
  VERDICTS.includes(value as RightsVerdict) ? value as RightsVerdict : null

/**
 * Read a `rights` block as the `.dsa` or the index row carries it. Returns
 * null when it is absent or unusable: a missing verdict, a missing or
 * over-long credit line. Unknown conditions are dropped, not fatal, so a
 * newer producer never blanks a stream in an older app.
 */
export function parseRights(value: unknown): StreamRights | null {
  const block = record(value)
  if (!block) return null
  const web = verdict(block.web)
  const broadcast = verdict(block.broadcast)
  const commercial = verdict(block.commercial)
  const credit = record(block.credit)
  const line = text(credit?.line)
  if (!web || !broadcast || !commercial || !credit || !line || line.length > CREDIT_LINE_MAX) return null
  return {
    web,
    broadcast,
    commercial,
    credit: {
      required: REQUIREMENTS.includes(credit.required as CreditRequirement) ? credit.required as CreditRequirement : 'required',
      line,
      placement: PLACEMENTS.includes(credit.placement as CreditPlacement) ? credit.placement as CreditPlacement : 'on-image',
    },
    licences: (Array.isArray(block.licences) ? block.licences : []).flatMap((entry): RightsLicence[] => {
      const licence = record(entry)
      const name = text(licence?.name)
      return licence && name ? [{ name, url: text(licence.url), holder: text(licence.holder) }] : []
    }),
    conditions: (Array.isArray(block.conditions) ? block.conditions : [])
      .filter((c): c is RightsCondition => RIGHTS_CONDITIONS.includes(c as RightsCondition)),
    reviewedBy: text(block.reviewedBy),
    reviewedAt: day(block.reviewedAt),
    recheckBy: day(block.recheckBy),
    notes: text(block.notes),
  }
}

/** True once the recheck date has passed (`today` is YYYY-MM-DD, UTC). */
export function recheckPassed(rights: StreamRights, today: string): boolean {
  return rights.recheckBy !== null && today > rights.recheckBy
}

/**
 * The verdict that applies to a use today: a stream never reviewed, or
 * whose recheck date has passed, is needs check.
 */
export function effectiveVerdict(rights: StreamRights | null | undefined, use: RightsUse, today: string): RightsVerdict {
  if (!rights || !rights.reviewedAt || recheckPassed(rights, today)) return 'needs_check'
  return rights[use]
}

export const isCleared = (v: RightsVerdict): boolean => v === 'cleared' || v === 'cleared_with_credit'

/**
 * May this stream go into a paid render? Always yes while gating is off
 * (verdicts are shown, not enforced); once on, only a cleared commercial
 * verdict passes.
 */
export function allowedInPaidRender(
  rights: StreamRights | null | undefined, today: string, gating: boolean = RIGHTS_GATING,
): boolean {
  return !gating || isCleared(effectiveVerdict(rights, 'commercial', today))
}

/**
 * The line a render burns in: the reviewed credit when there is one, else
 * the `.dsa` attribution, else nothing (the caller then refuses to render a
 * paid job, since every source asks for at least a credit).
 */
export function renderCreditLine(rights: StreamRights | null | undefined, attributionText: string | null | undefined): string | null {
  return rights?.credit.line ?? (attributionText?.trim() || null)
}

/** Today's date as YYYY-MM-DD in UTC, the form the review dates use. */
export const todayUtc = (now: Date = new Date()): string => now.toISOString().slice(0, 10)
